import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  OrderedList,
  ListItem,
  useColorModeValue,
  Divider,
  Icon,
  HStack,
  Badge,
  Button,
} from '@chakra-ui/react';
import { FiBook, FiUsers, FiShield, FiAlertCircle } from 'react-icons/fi';

const Section = ({ icon, title, children }) => (
  <Box 
    w="full" 
    bg={useColorModeValue('white', 'gray.800')} 
    p={6} 
    borderRadius="lg" 
    shadow="md"
    transition="all 0.3s"
    _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
  >
    <HStack spacing={4} mb={4}>
      <Icon as={icon} w={6} h={6} color="brand.500" />
      <Heading size="md">{title}</Heading>
    </HStack>
    {children}
  </Box>
);

const TermsOfService = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  return (
    <Box bg={bgColor} minH="100vh" py={12}>
      <Container maxW="container.md">
        <VStack spacing={8} align="start">
          <VStack align="start" spacing={3} w="full">
            <Badge colorScheme="brand" fontSize="sm" px={3} py={1}>
              Legal Agreement
            </Badge>
            <Heading size="xl" color={useColorModeValue('gray.800', 'white')}>
              Terms of Service
            </Heading>
            <Text color="gray.500">
              Last updated: {new Date().toLocaleDateString()}
            </Text>
            <Divider my={4} borderColor={useColorModeValue('gray.200', 'gray.700')} />
          </VStack>

          <Box 
            w="full" 
            bg="yellow.50" 
            color="yellow.800" 
            p={4} 
            borderRadius="md"
            borderLeft="4px solid"
            borderColor="yellow.400"
          >
            <HStack spacing={3}>
              <Icon as={FiAlertCircle} w={5} h={5} />
              <Text fontWeight="medium">
                Please read these terms carefully before using our services.
              </Text>
            </HStack>
          </Box>

          <VStack spacing={6} w="full">
            <Section icon={FiBook} title="1. Acceptance of Terms">
              <Text lineHeight="tall">
                By accessing and using Budget Brilliance's services, you accept and agree to be bound 
                by the terms and provisions of this agreement. If you do not agree to these terms, 
                please do not use our services.
              </Text>
            </Section>

            <Section icon={FiUsers} title="2. User Responsibilities">
              <OrderedList spacing={4} stylePosition="inside" pl={6}>
                <ListItem>
                  <Text fontWeight="medium" color="brand.500">Account Maintenance</Text>
                  <Text mt={1}>Keep all account information accurate and up-to-date</Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="medium" color="brand.500">Security</Text>
                  <Text mt={1}>Protect account credentials and maintain confidentiality</Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="medium" color="brand.500">Compliance</Text>
                  <Text mt={1}>Follow all applicable laws and regulatory requirements</Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="medium" color="brand.500">Professional Conduct</Text>
                  <Text mt={1}>Maintain professional standards in all platform interactions</Text>
                </ListItem>
              </OrderedList>
            </Section>

            <Section icon={FiShield} title="3. Service Rules">
              <Text lineHeight="tall" mb={4}>
                Users agree not to engage in any activity that interferes with or disrupts the service
                or the servers and networks connected to the service.
              </Text>
              <Box 
                p={4} 
                bg={useColorModeValue('red.50', 'red.900')} 
                color={useColorModeValue('red.800', 'red.200')} 
                borderRadius="md"
              >
                <Heading size="sm" mb={2}>Prohibited Activities:</Heading>
                <OrderedList spacing={2} stylePosition="inside" pl={6}>
                  <ListItem>Unauthorized access to other users' accounts</ListItem>
                  <ListItem>Distribution of malware or harmful code</ListItem>
                  <ListItem>Attempt to breach platform security</ListItem>
                  <ListItem>Sharing of confidential information</ListItem>
                </OrderedList>
              </Box>
            </Section>
          </VStack>

          <Button 
            colorScheme="brand" 
            size="lg" 
            w="full"
            mt={4}
          >
            I Accept These Terms
          </Button>
        </VStack>
      </Container>
    </Box>
  );
};

export default TermsOfService; 